import { html } from '../../mixins/katapult-element';
import { KatapultEditorTemplate } from './katapult-editor-template';
import '../katapult-elements/katapult-button.js';
import '../input-element/input-element.js';
import { GeneratePushID } from '../../modules/GeneratePushID.js';

class KatapultCompatibleUnitEditor extends KatapultEditorTemplate {
  static get template() {
    return html`
      ${super.style}
      <style>
        :host {
          --paper-input-container-disabled: {
            opacity: 0.7;
          };
        }
        paper-row[disabled] {
          opacity: 0.7;
          pointer-events: none;
        }
        paper-tabs {
          --paper-tabs-selection-bar-color: var(--secondary-color);
          --paper-tab-ink: var(--secondary-color);
          --paper-tabs-content: {
            display: flex;
            min-width: 100%;
          };
        }
        paper-tab {
          --paper-tab: {
            font-size: 12pt;
            font-weight: normal;
          };
          --paper-tab-content-focused: {
            font-weight: normal;
          };
        }
        #specSourceLabel {
          position: absolute;
          top: 0;
          right: 0;
          padding: 1.25em 1em;
          font-style: italic;
          color: var(--primary-text-color-faded);
          background: white;
        }
      </style>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes/wire_spec" data="{{wireSpecAttribute}}">
      </katapult-firebase-worker>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/pole_loading/tension_districts" data="{{tensionDistricts}}">
      </katapult-firebase-worker>

      <template is="dom-if" if="[[itemData.source]]">
        <div id="specSourceLabel">Source: [[itemData.source]]</div>
      </template>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          step$="[[step.id]]"
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <!-- Description -->
          <template is="dom-if" if="[[equal(step.id, 'description')]]" restamp>
            <paper-textarea
              slot="body"
              style="width:500px;"
              label="Description"
              value="[[_itemData.description]]"
              data-path="_itemData.description"
              on-value-changed="updateProperty"
              no-label-float
              disabled="[[!fullyEditable]]"
            >
            </paper-textarea>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Hours -->
          <template is="dom-if" if="[[equal(step.id, 'hours')]]" restamp>
            <paper-table rounded outline dividers overflow-hidden slot="body" style="min-width: 500px;">
              <paper-row grey short>
                <paper-cell style="gap: 8px;">
                  <katapult-icon icon="engineering" outline></katapult-icon>
                  <div>Action</div>
                </paper-cell>
                <paper-cell style="gap: 8px;">
                  <katapult-icon icon="electric_bolt" outline></katapult-icon>
                  <div>Hot</div>
                </paper-cell>
                <paper-cell style="gap: 8px;">
                  <katapult-icon icon="schedule"></katapult-icon>
                  <div>Quantity</div>
                </paper-cell>
                <paper-cell icon></paper-cell>
              </paper-row>
              <template is="dom-repeat" items="[[getItemsArray(_itemData.hours.*)]]" mutable-data>
                <paper-row>
                  <paper-cell input>
                    <katapult-drop-down
                      label="Action"
                      items="[[actionTypes]]"
                      value="[[item.action]]"
                      data-path="_itemData.hours.$(model.item.$key).action"
                      on-value-changed="updateProperty"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    ></katapult-drop-down>
                  </paper-cell>
                  <paper-cell input>
                    <paper-checkbox
                      checked="[[item.hot]]"
                      data-path="_itemData.hours.$(model.item.$key).hot"
                      on-checked-changed="updateProperty"
                      disabled="[[!fullyEditable]]"
                    >
                    </paper-checkbox>
                  </paper-cell>
                  <paper-cell input>
                    <paper-input
                      label="Quantity"
                      value="[[item.quantity]]"
                      data-path="_itemData.hours.$(model.item.$key).quantity"
                      on-value-changed="updateProperty"
                      data-parse="float"
                      no-label-float
                      disabled="[[!fullyEditable]]"
                    >
                    </paper-input>
                  </paper-cell>
                  <paper-cell icon-button>
                    <katapult-button
                      iconOnly
                      noBorder
                      icon="delete"
                      data-path="_itemData.hours.$(model.item.$key)"
                      data-value="$null"
                      on-click="updateProperty"
                    ></katapult-button>
                  </paper-cell>
                </paper-row>
              </template>
              <paper-row ripple light-grey short pointer on-click="addHours">
                <paper-cell style="gap: 8px;" justify-center>
                  <material-icon icon="add_circle"></material-icon>
                  <div>Add Hours</div>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Accounting -->
          <template is="dom-if" if="[[equal(step.id, 'accounting')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input expand>
                <paper-cell>
                  <katapult-multi-drop-down
                    style="width:500px;"
                    label="Accounting"
                    items="[[accountingTypes]]"
                    value="[[_itemData.accounting]]"
                    data-path="_itemData.accounting"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </katapult-multi-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Specify Quantity In Feet -->
          <template is="dom-if" if="[[equal(step.id, 'specify_qty_in_ft')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.specify_qty_in_ft}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              noBorder
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.specify_qty_in_ft"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.specify_qty_in_ft"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <!-- Material Amount -->
          <template is="dom-if" if="[[equal(step.id, 'material_amount')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Material Amount"
                    value="[[_itemData.material_amount]]"
                    data-path="_itemData.material_amount"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- No Flagging -->
          <template is="dom-if" if="[[equal(step.id, 'no_flagging')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.no_flagging}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              noBorder
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.no_flagging"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.no_flagging"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <!-- GIS CU -->
          <template is="dom-if" if="[[equal(step.id, 'gis_cu')]]" restamp>
            <paper-cell icon slot="header">
              <paper-checkbox disabled="[[!fullyEditable]]" checked="{{_itemData.gis_cu}}"></paper-checkbox>
            </paper-cell>
            <katapult-button
              noBorder
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetTrue"
              path="_itemData.gis_cu"
              class="dialog-option"
              style="margin-right: 8px"
              >Yes</katapult-button
            >
            <katapult-button
              color="var(--secondary-color)"
              slot="current-step-body"
              on-click="buttonSetFalse"
              path="_itemData.gis_cu"
              class="dialog-option"
              >No</katapult-button
            >
          </template>

          <!-- Usage -->
          <template is="dom-if" if="[[equal(step.id, 'usage')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <katapult-drop-down
                    label="Usage"
                    items="[[usageTypes]]"
                    value="[[_itemData.usage]]"
                    data-path="_itemData.usage"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </katapult-drop-down>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Sort Order -->
          <template is="dom-if" if="[[equal(step.id, 'order')]]" restamp>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Sort Order"
                    value="[[_itemData.order]]"
                    data-path="_itemData.order"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Tags -->
          <template is="dom-if" if="[[equal(step.id, 'tags')]]" restamp>
            <material-icon icon="help" slot="header">
              <katapult-tooltip>Comma separated list of tags that can be used for searching in CU Entry</katapult-tooltip>
            </material-icon>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Tags"
                    value="[[_itemData.tags]]"
                    data-path="_itemData.tags"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>

          <!-- Type -->
          <template is="dom-if" if="[[equal(step.id, 'type')]]" restamp>
            <material-icon icon="help" slot="header">
              <katapult-tooltip
                >i.e. removeonly, pole, qtyfeet, nontransfer, transferonly, installonly, fpole, opensecondary</katapult-tooltip
              >
            </material-icon>
            <paper-table light-grey rounded outline slot="body">
              <paper-row input>
                <paper-cell>
                  <paper-input
                    label="Type"
                    value="[[_itemData.type]]"
                    data-path="_itemData.type"
                    on-value-changed="updateProperty"
                    no-label-float
                    disabled="[[!fullyEditable]]"
                  >
                  </paper-input>
                </paper-cell>
              </paper-row>
            </paper-table>
            <katapult-button style="margin-left: 16px; min-width: 5.14em;" slot="current-step-body" on-click="nextStep"
              >Skip</katapult-button
            >
          </template>
        </katapult-editor-step>
      </template>
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      actionTypes: {
        type: Array,
        value: () => ['Install', 'Remove', 'Transfer']
      },
      accountingTypes: {
        type: Array,
        value: () => ['Capital', 'Maintenance', 'Operations']
      },
      usageTypes: {
        type: Array,
        value: () => ['Overhead', 'Underground']
      }
    };
  }

  static get observers() {
    return [];
  }

  constructor() {
    super();
  }

  ready() {
    super.ready();

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'description',
        title: 'Description',
        trigger: () => this.get('_itemData.description') != null
      },
      {
        id: 'hours',
        title: 'Hours',
        trigger: () => this.get('_itemData.hours') != null
      },
      {
        id: 'accounting',
        title: 'Accounting',
        trigger: () => this.get('_itemData.accounting') != null
      },
      {
        id: 'specify_qty_in_ft',
        title: 'Specify Quantity In Feet',
        trigger: () => this.get('_itemData.specify_qty_in_ft') != null
      },
      {
        id: 'material_amount',
        title: 'Material Amount',
        trigger: () => this.get('_itemData.material_amount') != null
      },
      {
        id: 'no_flagging',
        title: 'No Flagging',
        trigger: () => this.get('_itemData.no_flagging') != null
      },
      {
        id: 'gis_cu',
        title: 'GIS CU',
        trigger: () => this.get('_itemData.gis_cu') != null
      },
      {
        id: 'usage',
        title: 'Usage',
        trigger: () => this.get('_itemData.usage') != null
      },
      {
        id: 'order',
        title: 'Sort Order',
        trigger: () => this.get('_itemData.order') != null
      },
      {
        id: 'tags',
        title: 'Searchable Tags',
        trigger: () => this.get('_itemData.tags') != null
      },
      {
        id: 'type',
        title: 'Type',
        trigger: () => this.get('_itemData.type') != null
      }
    ]);
  }

  labelChanged(label) {
    this.set('_itemData.cu', label);
  }

  addHours(e) {
    this.deepSet(`_itemData.hours.${GeneratePushID()}`, { action: '', hot: false, quantity: '' });
  }
}

customElements.define('katapult-compatible-unit-editor', KatapultCompatibleUnitEditor);
